import api, { API } from "@/lib/api";

// Offline "audio pack": every TTS segment of the handbook in one language, stored in CacheStorage.
const CACHE_NAME = "manasik-audiobook-v1";
const INDEX_KEY = "audiobook_offline";
const BASE = API.replace(/\/api$/, "");
const hasCaches = () => typeof window !== "undefined" && "caches" in window;

export const readPacks = () => {
  try { return JSON.parse(localStorage.getItem(INDEX_KEY) || "{}"); } catch { return {}; }
};
const writePacks = (p) => localStorage.setItem(INDEX_KEY, JSON.stringify(p));
export const getPack = (lang) => readPacks()[lang] || null;
export const countSegments = (chapters) => chapters.reduce((n, c) => n + c.segments.length, 0);
const absUrl = (u) => (u.startsWith("http") ? u : `${BASE}${u}`);

// Cheap signature of all chapter texts so a pack is flagged stale after content updates.
export function chaptersSig(chapters) {
  let h = 5381;
  for (const c of chapters) for (const s of c.segments) for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return String(h);
}

export const packState = (pack, sig, voiceId, total) => {
  if (!pack) return "none";
  if (pack.sig !== sig || pack.voiceId !== voiceId) return "stale";
  return Object.keys(pack.urls).length >= total ? "done" : "partial";
};

// Object URL for a segment stored offline (null if not available).
export async function offlineSegmentUrl(lang, voiceId, sig, c, s) {
  const pack = getPack(lang);
  if (!pack || !hasCaches() || pack.voiceId !== voiceId || pack.sig !== sig) return null;
  const url = pack.urls[`${c}:${s}`];
  if (!url) return null;
  const res = await (await caches.open(CACHE_NAME)).match(url);
  return res ? URL.createObjectURL(await res.blob()) : null;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const isNetworkError = (e) =>
  e?.code === "ERR_NETWORK" || (e instanceof TypeError) || (!e?.response && e?.name !== "AbortError" && e?.code !== "ERR_CANCELED" && !!e?.message?.match(/network|fetch/i));
const isFatal = (e) => e?.name === "AbortError" || e?.code === "ERR_CANCELED" || (e?.response && e.response.status < 500);

// Retry transient failures (network / 5xx) with 1s, 2s, 4s backoff; never retry 4xx or aborts.
async function withRetry(fn, signal, attempts = 3) {
  for (let i = 0; ; i++) {
    try { return await fn(); } catch (e) {
      if (isFatal(e) || i >= attempts - 1 || signal?.aborted) throw e;
      await sleep(1000 * 2 ** i);
    }
  }
}

// Download (or resume) the whole book for one language with `concurrency` parallel segments.
// Progress ({done,total,bytes,etaSec}) is persisted after every segment so it can resume anytime.
export async function downloadPack({ lang, voiceId, chapters, onProgress, signal, concurrency = 3 }) {
  const sig = chaptersSig(chapters);
  const total = countSegments(chapters);
  const packs = readPacks();
  let pack = packs[lang];
  if (!pack || pack.sig !== sig || pack.voiceId !== voiceId) pack = { voiceId, sig, total, urls: {}, bytes: 0, savedAt: null };
  const cache = hasCaches() ? await caches.open(CACHE_NAME) : null;
  const queue = [];
  chapters.forEach((ch, c) => ch.segments.forEach((_, s) => { if (!pack.urls[`${c}:${s}`]) queue.push({ c, s }); }));
  let done = Object.keys(pack.urls).length;
  let doneThisRun = 0;
  const startedAt = Date.now();
  let failure = null;
  const report = () => onProgress?.({
    done, total, bytes: pack.bytes,
    etaSec: doneThisRun ? Math.round(((Date.now() - startedAt) / doneThisRun) * (total - done) / 1000) : null,
  });
  report();

  const worker = async () => {
    while (queue.length && !failure) {
      if (signal?.aborted) throw new DOMException("aborted", "AbortError");
      const { c, s } = queue.shift();
      try {
        const { url, blob } = await withRetry(async () => {
          const { data } = await api.post("/tts", { text: chapters[c].segments[s], voice_id: voiceId }, { signal });
          const u = absUrl(data.url);
          const res = await fetch(u, { credentials: "include", signal });
          if (!res.ok) { const err = new Error("fetch audio failed"); err.response = { status: res.status }; throw err; }
          return { url: u, blob: await res.blob() };
        }, signal);
        if (cache) await cache.put(url, new Response(blob, { headers: { "Content-Type": "audio/mpeg" } }));
        pack.urls[`${c}:${s}`] = url;
        pack.bytes += blob.size;
        pack.savedAt = Date.now();
        done++; doneThisRun++;
        packs[lang] = pack;
        writePacks(packs);
        report();
      } catch (e) {
        failure = failure || e;
        throw e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, queue.length)) }, worker));
  return pack;
}

export async function deletePack(lang) {
  const packs = readPacks();
  const pack = packs[lang];
  if (pack && hasCaches()) {
    const cache = await caches.open(CACHE_NAME);
    await Promise.all(Object.values(pack.urls).map((u) => cache.delete(u)));
  }
  delete packs[lang];
  writePacks(packs);
}
